import { useCallback } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import { type useSetUserEmailMarketingEnabledMutation_user$key } from '@/__generated__/useSetUserEmailMarketingEnabledMutation_user.graphql'
import {
  type useSetUserEmailMarketingEnabledMutation as UseSetUserEmailMarketingEnabledMutation,
  type useSetUserEmailMarketingEnabledMutation$data
} from '@/__generated__/useSetUserEmailMarketingEnabledMutation.graphql'

type CommitAsyncFn = (enabled: boolean) => Promise<useSetUserEmailMarketingEnabledMutation$data>

export const useSetUserEmailMarketingEnabledMutation = (
  userRef: useSetUserEmailMarketingEnabledMutation_user$key | null | undefined
): [CommitAsyncFn, boolean] => {
  const userData = useFragment(
    graphql`
      fragment useSetUserEmailMarketingEnabledMutation_user on User {
        rowId
      }
    `,
    userRef
  )
  const [commit, isPending] = useMutation<UseSetUserEmailMarketingEnabledMutation>(graphql`
    mutation useSetUserEmailMarketingEnabledMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          isEmailMarketingEnabled
          updatedAt
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (enabled: boolean) =>
      new Promise<useSetUserEmailMarketingEnabledMutation$data>((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              resolve(response)
            }
          },
          // transport-level failures (offline, 5xx, WS drop) invoke onError, not onCompleted — without
          // this the promise would never settle and the caller would hang forever.
          onError: (error) => reject(error),
          variables: {
            input: {
              rowId: userData?.rowId,
              patch: {
                isEmailMarketingEnabled: enabled
              }
            }
          }
        })
      ),
    [commit, userData?.rowId]
  )

  return [commitAsync, isPending]
}
